<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  deleteInternationalAccountAction,
  deleteInternationalPaypalAccountAction,
  deleteInternationalRevolutAccountAction,
  deleteInternationalWiseAccountAction,
  deleteIranianAccountAction,
  deleteIranianSrcAccountAction,
} from '@/api/functions/bank-account/delete-bank-account.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import UpdateBankAccountModal from './UpdateBankAccountModal.vue'

const props = defineProps({
  account: { type: Object as () => Record<string, any>, required: true },
  userId: { type: Number },
})

const emit = defineEmits(['confirmAccount'])

const toast = useToast()
const showDeleteModal = ref(false)
const showUpdateModal = ref(false)
const accountType = computed(() => props.account?.type)
const isIranian = computed(() => ['iranianSource', 'iranianDestination'].includes(accountType.value))
const isInternational = computed(() => ['international', 'revolut', 'paypal', 'wise'].includes(accountType.value))
const hasIbanAndBic = computed(() => ['international', 'revolut', 'wise'].includes(accountType.value))

const accountTypeLabel = computed(() => {
  switch (accountType.value) {
    case 'iranianSource':
      return 'حساب مبداء ریالی'
    case 'iranianDestination':
      return 'حساب مقصد ریالی'
    case 'international':
    case 'revolut':
    case 'paypal':
    case 'wise':
      return 'حساب مقصد ارزی'
    default:
      return ''
  }
})

const currencyAccountTypeLabel = computed(() => {
  switch (accountType.value) {
    case 'revolut':
      return 'Revolut'
    case 'paypal':
      return 'Paypal'
    case 'international':
      return 'Bank Transfer'
    case 'wise':
      return 'Wise'
    default:
      return ''
  }
})

const deleteAccountMutation = useMutationApi({
  mutationFn: async () => {
    switch (props.account.type) {
      case 'iranianDestination':
        return deleteIranianAccountAction(props.account.id)
      case 'iranianSource':
        return deleteIranianSrcAccountAction(props.account.id)
      case 'international':
        return deleteInternationalAccountAction(props.account.id)
      case 'revolut':
        return deleteInternationalRevolutAccountAction(props.account.id)
      case 'paypal':
        return deleteInternationalPaypalAccountAction(props.account.id)
      case 'wise':
        return deleteInternationalWiseAccountAction(props.account.id)
      default:
        throw new Error('نوع حساب شناسایی نشد')
    }
  },
  onSuccess: () => {
    toast.success('حساب با موفقیت حذف شد')
    emit('confirmAccount')
    showDeleteModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleDeleteDiscount = async () => {
  deleteAccountMutation.mutate()
  // emit('confirmAccount');
}

const handleAccountUpdated = () => {
  showUpdateModal.value = false
  emit('confirmAccount')
}
</script>

<template>
  <div class="bg-white rounded-xl p-5 lg:p-8 lg:pb-6 relative flex flex-col-reverse lg:flex-col gap-6 lg:gap-0">

    <ConfirmModal v-model="showDeleteModal" title="حذف شماره حساب" :message="'آیا از حذف این شماره حساب اطمینان دارید؟'"
      confirm-text="بله حذف شود" cancel-text="بازگشت" @confirm="handleDeleteDiscount"
      @cancel="showDeleteModal = false" />

    <UpdateBankAccountModal v-model="showUpdateModal" :account="account" :userId="userId"
      @confirm="handleAccountUpdated" @cancel="showUpdateModal = false" />


    <div class="relative lg:absolute bottom-0 lg:top-8 lg:end-8">
      <div class=" w-full lg:w-fit flex  items-center">
        <Button @click="showUpdateModal = true" :isAdmin="true"
          class=" w-full text-nowrap lg:w-auto border-2  rounded-[10px] px-5! h-10! py-0 text-[13px] me-2">
          ویرایش
        </Button>
        <Button @click="showDeleteModal = true" :isAdmin="true" variant="outline"
          class=" w-full text-nowrap lg:w-auto border-2  rounded-[10px] px-5! h-10! py-0 text-[13px] me-2">
          حذف
        </Button>
      </div>
    </div>

    <div class="flex flex-col gap-6  text-black text-sm font-medium">
      <!-- Header -->
      <div class="w-full lg:w-[45%] flex items-center justify-center gap-2 lg:justify-between">
        نام صاحب حساب: {{ account?.ownerFullName }}
      </div>
      <!-- Body -->
      <div class="flex flex-col gap-6  text-sm text-black font-medium">
        <div class="flex items-center gap-1">
          <span>
            نام بانک: {{ account?.bankName }}
          </span>
        </div>

        <div v-if="hasIbanAndBic && account?.bicOrCardNumber" class="flex items-center gap-1">
          <span>شماره BIC code:</span>
          <span class="font-fa">
            {{ account?.bicOrCardNumber }}</span>
        </div>


        <div v-if="hasIbanAndBic && account?.ibanOrShebaNumber" class="flex items-center gap-1">
          <span>شماره IBAN:</span>
          <span class="font-fa">
            {{ account?.ibanOrShebaNumber }}</span>
        </div>

        <div v-if="accountType === 'revolut' && account?.revolutTag" class="flex items-center gap-1">
          <span>تگ Revolut:</span>
          <span class="font-fa">
            {{ account?.revolutTag }}</span>
        </div>
        
        <div v-if="isIranian && account?.ibanOrShebaNumber" class="flex items-center gap-1">
          <span>شماره شبا:</span>
          <span class="font-fa">
            {{ account?.ibanOrShebaNumber }}</span>
        </div>

        <div v-if="accountType === 'paypal' && account?.paypalEmail" class="flex items-center gap-1">
          <span>ایمیل paypal:</span>
          <span class="font-fa">
            {{ account?.paypalEmail }}</span>
        </div>

        <div v-if="accountType === 'paypal' && account?.paypalId" class="flex items-center gap-1">
          <span>آیدی paypal:</span>
          <span class="font-fa">
            {{ account?.paypalId }}</span>
        </div>

        <div v-if="isIranian && account?.bicOrCardNumber" class="flex items-center gap-1">
          <span>شماره کارت:</span>
          <span class="font-fa">{{ account?.bicOrCardNumber }}</span>
        </div>



        <div class="flex items-center gap-1">
          <span>نوع حساب:</span>
          <span>
            {{ accountTypeLabel }}
          </span>
        </div>

        <div v-if="isInternational" class="flex items-center gap-1">
          <span>نوع حساب ارزی:</span>
          <span>
            {{ currencyAccountTypeLabel }}
          </span>
        </div>

        <div v-if="isIranian" class="flex items-center gap-1">
          <span>وضعیت:</span>
          <div class="flex items-center  gap-1  font-normal "
            :class="account.isVerified ? 'text-[#008000]' : 'text-[#ed145b]'">
            <img v-if="!account?.isVerified" src="/svg/ic_warning.svg" width="12" height="12" alt="warning-icon"
              class="cursor-pointer" />
            {{ account?.isVerified ? 'احرازهویت شده' : 'احراز هویت نشده' }}
          </div>
        </div>

      </div>
    </div>
  </div>

</template>
